import React, { useEffect, useMemo, useState } from 'react';
import { Form, Stack } from 'react-bootstrap';

import {
  mapToEditType,
  mapToEpisodeDetailsKey,
  mapValueToString,
} from './utils';
import {
  CombinedTypes,
  DateTypes,
  EditType,
  type AdditionalSectionValue,
} from './types';

import CareMapprDatePicker from '../../../DatePicker/CareMapprDatePicker';
import CareMapprTextField from '../../../Inputs/textField/CareMapprTextField';
import CareMapprIconButton from '../../../Button/CareMapprIconButton';
import ActionButtons from './EpisodeDetailActionButtons';
import {
  convertDateInstanceToDateString,
  convertDateStringToDateInstance,
  convertToFormattedDateString,
  YYYY_MM_DD,
} from '../../../../helpers';
import { Edit } from '../../../Icons';
import { usePatientProfile } from '../../hooks';
import DataMapperProfileBar from '../../../DataMapper/DataMapperProfileBar';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../../store';

interface EpisodeDetailFieldProps {
  label: string;
  value: AdditionalSectionValue;
  editable: boolean;
  italic?: boolean;
}
const EpisodeDetailField = ({
  label,
  value,
  editable,
  italic,
}: EpisodeDetailFieldProps) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const patientAccessToken = searchParams.get('access_token');
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  const { onSubmit, form, patientProfile } = usePatientProfile();
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value.value);
  const italicStyle = italic ? { fontStyle: 'italic' } : {};

  useEffect(() => {
    setCurrentValue(value.value);
    setIsEditing(false);
  }, [patientProfile?.id, label, value.value]);

  const onCancel = () => {
    setIsEditing(false);
    setCurrentValue(value.value);
  };

  const onConfirm = () => {
    const detailsKey = mapToEpisodeDetailsKey(label);
    if (!patientProfile?.clinics?.length) return;

    const normalizeDetailsObject = (details: any): Record<string, any> => {
      if (Array.isArray(details)) {
        return details.reduce<Record<string, any>>((acc, cur) => {
          const key =
            typeof cur?.meta_name === 'string' ? cur.meta_name.trim() : '';
          if (!key) return acc;
          const value =
            typeof cur?.meta_value === 'string'
              ? cur.meta_value
              : cur?.meta_value;
          acc[key] = value;
          return acc;
        }, {});
      }

      if (details && typeof details === 'object') {
        return { ...details };
      }

      return {};
    };

    const upsertEpisodeDetail = (details: any, key: string, nextValue: any) => {
      const normalized = normalizeDetailsObject(details);
      return { ...normalized, [key]: nextValue };
    };

    const updatedClinics = patientProfile.clinics.map((clinic) => {
      const episodes =
        Array.isArray(clinic.episodes) && clinic.episodes.length > 0
          ? clinic.episodes
          : [
              {
                episode_no: clinic.episode_no ?? null,
                episode_closed: clinic.episode_closed ?? false,
                episode_opened_date: clinic.episode_opened_date ?? undefined,
                episode_closed_date: clinic.episode_closed_date ?? undefined,
                primary_teams: Array.isArray(clinic.primaryTeams)
                  ? clinic.primaryTeams.join(',')
                  : undefined,
                details: [],
              },
            ];

      const updatedEpisodes = episodes.map((episode: any) => ({
        ...episode,
        details: upsertEpisodeDetail(
          episode?.details,
          detailsKey,
          currentValue,
        ),
      }));

      return { ...clinic, episodes: updatedEpisodes };
    });
    const updatedPatientProfile = {
      ...patientProfile,
      clinics: updatedClinics,
    };
    const formData = DataMapperProfileBar(
      form.formState,
      updatedPatientProfile,
      patientAccessToken,
      selectedTenancy,
      [],
    );
    setIsEditing(false);
    onSubmit(formData);
  };

  const convertedValue: string | React.ReactNode = useMemo(() => {
    if (CombinedTypes.includes(label)) {
      return (
        <div className="d-flex align-items-center gap-3">
          <div className="d-flex align-items-center gap-1">
            {currentValue && <span>{currentValue as string}</span>}
            {Boolean(value.trend) ? (
              <span
                className={`pdf-font-size-25 ${
                  value.trend && value.trend > 0
                    ? 'egfr-trend-up'
                    : 'egfr-trend-down'
                }`}
              >
                {`${value.trend && value.trend > 0 ? '+' : '-'}${value.trend}`}
              </span>
            ) : (
              '-'
            )}
          </div>
          {value.dateStr && (
            <span className="egfr-date pdf-font-size-25">{`(${convertToFormattedDateString(value.dateStr)})`}</span>
          )}
        </div>
      );
    } else if (DateTypes.includes(label)) {
      return currentValue
        ? convertToFormattedDateString(currentValue as string)
        : '-';
    }

    return mapValueToString(currentValue);
  }, [currentValue, label, value.dateStr, value.trend]);

  const mapToEditComponent = (field: string): React.ReactNode | null => {
    const editType = mapToEditType(field);
    switch (editType) {
      case EditType.DATE:
        return (
          <div
            style={{ zIndex: 50 }}
            className="d-flex align-items-center gap-1 my-1"
          >
            <CareMapprDatePicker
              className="width-120"
              label={undefined}
              selectedDate={convertDateStringToDateInstance(
                currentValue as string,
              )}
              onChange={(date: Date) => {
                const dateString = convertDateInstanceToDateString(
                  date,
                  YYYY_MM_DD,
                );
                setCurrentValue(dateString);
              }}
              minDate={undefined}
              maxDate={undefined}
            />
            <ActionButtons onConfirm={onConfirm} onCancel={onCancel} />
          </div>
        );
      case EditType.TEXT:
        return (
          <div
            style={{ zIndex: 50 }}
            className="d-flex align-items-center gap-1 my-1"
          >
            <CareMapprTextField
              className="width-120"
              value={currentValue as string}
              onChange={(e) => setCurrentValue(e.target.value)}
            />
            <ActionButtons onConfirm={onConfirm} onCancel={onCancel} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Stack direction="horizontal" gap={2} className="labelRow">
      <Form.Label className="footer-label">{label}</Form.Label>
      {!isEditing && (
        <>
          <Form.Label
            className="footer-value pdf-font-size-25"
            style={italicStyle}
          >
            {convertedValue}
          </Form.Label>
          {editable && (
            <CareMapprIconButton
              className="mb-2 min-width-auto mt-0"
              icon={<Edit size={16} />}
              onClick={() => {
                if (
                  DateTypes.includes(label) &&
                  (currentValue === null ||
                    currentValue === undefined ||
                    String(currentValue).trim().length === 0)
                ) {
                  setCurrentValue(
                    convertDateInstanceToDateString(new Date(), YYYY_MM_DD),
                  );
                }
                setIsEditing(true);
              }}
            />
          )}
        </>
      )}

      {isEditing && mapToEditComponent(label)}
    </Stack>
  );
};

export default EpisodeDetailField;
